import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, 
  Filter, 
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  Upload,
  ExternalLink,
  Play,
  Pause,
  RefreshCw,
  BarChart3,
  Users,
  Video,
  Link,
  Shield,
  Clock,
  Star,
  TrendingUp,
  FileText,
  Settings,
  Database,
  Globe,
  Instagram,
  Youtube,
  Facebook,
  Twitter,
  MessageSquare
} from 'lucide-react';

interface ExerciseVideoMatrix {
  exerciseId: string;
  exerciseName: string;
  category: string;
  equipment: string;
  difficulty: string;
  videos: VideoEntry[];
  lastUpdated: Date;
  status: 'active' | 'pending' | 'archived' | 'needs_review';
}

interface VideoEntry {
  id: string;
  videoUrl: string;
  videoSource: 'youtube' | 'vimeo' | 'instagram' | 'tiktok' | 'custom' | 'uploaded';
  videoId: string;
  videoChannel: string;
  videoChannelUrl?: string;
  videoAttribution: string;
  videoDuration?: number;
  videoThumbnail?: string;
  videoTitle?: string;
  videoDescription?: string;
  addedBy: string;
  addedAt: Date;
  lastVerified: Date;
  verificationStatus: 'verified' | 'pending' | 'failed' | 'expired';
  isPrimary: boolean;
  isAlternative: boolean;
  isProgression: boolean;
  isRegression: boolean;
  socialMediaLinks: {
    youtube?: string;
    instagram?: string;
    tiktok?: string;
    facebook?: string;
    twitter?: string;
  };
  viewCount: number;
  shareCount: number;
  likeCount: number;
  lastViewed?: Date;
  contentFlags: {
    hasAttribution: boolean;
    hasSafetyDisclaimer: boolean;
    isEducational: boolean;
    isAppropriate: boolean;
    needsReview: boolean;
  };
  legalStatus: {
    copyrightCompliant: boolean;
    attributionComplete: boolean;
    educationalUseOnly: boolean;
    lastLegalReview?: Date;
    legalNotes?: string;
  };
}

const AdminDashboardPanel: React.FC = () => {
  const [matrix, setMatrix] = useState<ExerciseVideoMatrix[]>([]);
  const [filteredMatrix, setFilteredMatrix] = useState<ExerciseVideoMatrix[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoEntry | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [analytics, setAnalytics] = useState<any>(null);

  useEffect(() => {
    loadExerciseVideoMatrix();
  }, []);

  useEffect(() => {
    filterMatrix();
  }, [matrix, searchQuery, selectedCategory, selectedStatus, selectedSource]);

  const loadExerciseVideoMatrix = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockMatrix: ExerciseVideoMatrix[] = [
        {
          exerciseId: 'ex_1',
          exerciseName: 'Push-up',
          category: 'strength',
          equipment: 'bodyweight',
          difficulty: 'beginner',
          videos: [
            {
              id: 'vid_1',
              videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
              videoSource: 'youtube',
              videoId: '4uQtPyWkXcc',
              videoChannel: 'Athlean-X',
              videoChannelUrl: 'https://www.youtube.com/@athleanx',
              videoAttribution: 'Video provided by Athlean-X — used for educational purposes only',
              videoDuration: 180,
              videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg',
              videoTitle: 'Perfect Push-Up Form',
              videoDescription: 'Learn the correct push-up form and technique',
              addedBy: 'admin_1',
              addedAt: new Date('2024-01-15'),
              lastVerified: new Date('2024-01-15'),
              verificationStatus: 'verified',
              isPrimary: true,
              isAlternative: false,
              isProgression: false,
              isRegression: false,
              socialMediaLinks: {
                youtube: 'https://www.youtube.com/@athleanx',
                instagram: 'https://www.instagram.com/athleanx'
              },
              viewCount: 1250,
              shareCount: 45,
              likeCount: 89,
              lastViewed: new Date('2024-01-20'),
              contentFlags: {
                hasAttribution: true,
                hasSafetyDisclaimer: true,
                isEducational: true,
                isAppropriate: true,
                needsReview: false
              },
              legalStatus: {
                copyrightCompliant: true,
                attributionComplete: true,
                educationalUseOnly: true,
                lastLegalReview: new Date('2024-01-15')
              }
            },
            {
              id: 'vid_2',
              videoUrl: 'https://www.instagram.com/p/ABC123/',
              videoSource: 'instagram',
              videoId: 'ABC123',
              videoChannel: 'Fitness Influencer',
              videoChannelUrl: 'https://www.instagram.com/fitness_influencer',
              videoAttribution: 'Video provided by Fitness Influencer — used for educational purposes only',
              videoDuration: 60,
              videoThumbnail: 'https://example.com/instagram-thumb.jpg',
              videoTitle: 'Push-up Variations',
              videoDescription: 'Different push-up variations for all levels',
              addedBy: 'admin_2',
              addedAt: new Date('2024-01-16'),
              lastVerified: new Date('2024-01-16'),
              verificationStatus: 'pending',
              isPrimary: false,
              isAlternative: true,
              isProgression: false,
              isRegression: false,
              socialMediaLinks: {
                instagram: 'https://www.instagram.com/fitness_influencer'
              },
              viewCount: 450,
              shareCount: 12,
              likeCount: 23,
              contentFlags: {
                hasAttribution: true,
                hasSafetyDisclaimer: true,
                isEducational: true,
                isAppropriate: true,
                needsReview: true
              },
              legalStatus: {
                copyrightCompliant: false,
                attributionComplete: true,
                educationalUseOnly: true,
                lastLegalReview: new Date('2024-01-16'),
                legalNotes: 'Pending legal review'
              }
            }
          ],
          lastUpdated: new Date('2024-01-16'),
          status: 'active'
        },
        {
          exerciseId: 'ex_2',
          exerciseName: 'Goblet Squat',
          category: 'strength',
          equipment: 'dumbbells',
          difficulty: 'beginner',
          videos: [
            {
              id: 'vid_3',
              videoUrl: 'https://www.youtube.com/watch?v=4uQtPyWkXcc',
              videoSource: 'youtube',
              videoId: '4uQtPyWkXcc',
              videoChannel: 'Fitness Blender',
              videoChannelUrl: 'https://www.youtube.com/@fitnessblender',
              videoAttribution: 'Video provided by Fitness Blender — used for educational purposes only',
              videoDuration: 120,
              videoThumbnail: 'https://img.youtube.com/vi/4uQtPyWkXcc/maxresdefault.jpg',
              videoTitle: 'Goblet Squat Tutorial',
              videoDescription: 'Learn proper goblet squat form',
              addedBy: 'admin_1',
              addedAt: new Date('2024-01-10'),
              lastVerified: new Date('2024-01-10'),
              verificationStatus: 'verified',
              isPrimary: true,
              isAlternative: false,
              isProgression: false,
              isRegression: false,
              socialMediaLinks: {
                youtube: 'https://www.youtube.com/@fitnessblender'
              },
              viewCount: 890,
              shareCount: 34,
              likeCount: 67,
              contentFlags: {
                hasAttribution: true,
                hasSafetyDisclaimer: true,
                isEducational: true,
                isAppropriate: true,
                needsReview: false
              },
              legalStatus: {
                copyrightCompliant: true,
                attributionComplete: true,
                educationalUseOnly: true,
                lastLegalReview: new Date('2024-01-10')
              }
            }
          ],
          lastUpdated: new Date('2024-01-10'),
          status: 'active'
        }
      ];

      setMatrix(mockMatrix);

      // Mock analytics
      setAnalytics({
        totalExercises: 150,
        totalVideos: 450,
        videosBySource: {
          youtube: 300,
          vimeo: 50,
          instagram: 75,
          tiktok: 25
        },
        videosByStatus: {
          verified: 400,
          pending: 30,
          failed: 15,
          expired: 5
        },
        pendingReviews: 12,
        lastUpdated: new Date()
      });
    } catch (error) {
      console.error('Error loading exercise video matrix:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMatrix = () => {
    let filtered = matrix;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(exercise =>
        exercise.exerciseName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exercise.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exercise.equipment.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exercise.videos.some(video => 
          video.videoChannel.toLowerCase().includes(searchQuery.toLowerCase()) ||
          video.videoTitle?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(exercise => exercise.category === selectedCategory);
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(exercise => exercise.status === selectedStatus);
    }

    // Source filter
    if (selectedSource !== 'all') {
      filtered = filtered.filter(exercise => 
        exercise.videos.some(video => video.videoSource === selectedSource)
      );
    }

    setFilteredMatrix(filtered);
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'archived': return 'text-[var(--color-text)]' + ' ' + 'bg-gray-100'.replace('bg-gray-100', 'style={{ background: "var(--color-background-secondary)" }}');
      case 'needs_review': return 'bg-red-100 text-red-800';
      default: return 'text-[var(--color-text)]' + ' ' + 'bg-gray-100'.replace('bg-gray-100', 'style={{ background: "var(--color-background-secondary)" }}');
    }
  };

  const getVerificationStatusColor = (status: string): string => {
    switch (status) {
      case 'verified': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-orange-100 text-orange-800';
      default: return 'text-[var(--color-text)]' + ' ' + 'bg-gray-100'.replace('bg-gray-100', 'style={{ background: "var(--color-background-secondary)" }}');
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'youtube': return <Youtube className="w-4 h-4 text-red-500" />;
      case 'instagram': return <Instagram className="w-4 h-4 text-pink-500" />;
      case 'tiktok': return <MessageSquare className="w-4 h-4 text-black" />;
      case 'vimeo': return <Video className="w-4 h-4 text-blue-500" />;
      case 'facebook': return <Facebook className="w-4 h-4 text-blue-600" />;
      case 'twitter': return <Twitter className="w-4 h-4 text-blue-400" />;
      default: return <Video className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />;
    }
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sculptd-container-padding">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ 
            color: 'var(--color-text)',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>Admin Dashboard</h2>
          <p className="text-base" style={{ 
            color: 'var(--color-text-muted)',
            lineHeight: '1.6'
          }}>Manage exercise video matrix and content</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={() => setShowBulkImportModal(true)}
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button
            onClick={() => setShowAddVideoModal(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Video
          </Button>
        </div>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Database className="w-5 h-5 text-blue-500" />
                <div>
                  <div className="text-2xl font-bold">{analytics.totalExercises}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Total Exercises</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Video className="w-5 h-5 text-green-500" />
                <div>
                  <div className="text-2xl font-bold">{analytics.totalVideos}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Total Videos</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                <div>
                  <div className="text-2xl font-bold">{analytics.pendingReviews}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Pending Reviews</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <div>
                  <div className="text-2xl font-bold">{analytics.videosByStatus.verified}</div>
                  <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Verified Videos</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <div className="sculptd-card-elevated sculptd-sheen" style={{
        boxShadow: 'var(--shadow-card-separation)'
      }}>
        <div className="p-6">
          <div className="space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
              <Input
                placeholder="Search exercises, channels, or videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--color-border)', 
                    background: 'white',
                    color: 'var(--color-text)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Categories</option>
                  <option value="strength">Strength</option>
                  <option value="cardio">Cardio</option>
                  <option value="flexibility">Flexibility</option>
                  <option value="balance">Balance</option>
                  <option value="sports">Sports</option>
                  <option value="rehabilitation">Rehabilitation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text)' }}>Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--color-border)', 
                    background: 'white',
                    color: 'var(--color-text)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="archived">Archived</option>
                  <option value="needs_review">Needs Review</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video Source</label>
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:border-transparent"
                  style={{ 
                    borderColor: 'var(--color-border)', 
                    background: 'white',
                    color: 'var(--color-text)'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 2px rgba(58, 175, 169, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Sources</option>
                  <option value="youtube">YouTube</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="vimeo">Vimeo</option>
                  <option value="facebook">Facebook</option>
                  <option value="twitter">Twitter</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Exercise Video Matrix */}
      <div className="space-y-4">
        {filteredMatrix.map((exercise) => (
          <div key={exercise.exerciseId} className="sculptd-card-elevated sculptd-sheen" style={{
            boxShadow: 'var(--shadow-card-separation)'
          }}>
            <div className="sculptd-container-padding mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ 
                      color: 'var(--color-text)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>{exercise.exerciseName}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                        background: 'var(--color-secondary)', 
                        color: 'var(--color-white)' 
                      }}>
                        {exercise.category}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                        background: 'var(--color-charcoal)', 
                        color: 'var(--color-white)' 
                      }}>
                        {exercise.equipment}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                        background: exercise.difficulty === 'beginner' ? 'var(--color-success)' : 
                                   exercise.difficulty === 'intermediate' ? 'var(--color-warning)' : 'var(--color-error)', 
                        color: 'var(--color-white)' 
                      }}>
                        {exercise.difficulty}
                      </span>
                      <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                        background: exercise.status === 'active' ? 'var(--color-primary)' : 
                                   exercise.status === 'pending' ? 'var(--color-warning)' : 'var(--color-error)', 
                        color: 'var(--color-white)' 
                      }}>
                        {exercise.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{ 
                      color: 'var(--color-primary)',
                      textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                      {exercise.videos.length}
                    </div>
                    <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Videos</div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExpandedExercise(
                      expandedExercise === exercise.exerciseId ? null : exercise.exerciseId
                    )}
                  >
                    {expandedExercise === exercise.exerciseId ? 'Hide' : 'Show'} Videos
                  </Button>
                </div>
              </div>
            </div>

            {expandedExercise === exercise.exerciseId && (
              <div className="p-6">
                <div className="space-y-4">
                  {exercise.videos.map((video) => (
                    <div key={video.id} className="border rounded-lg sculptd-container-padding">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          {/* Video Thumbnail */}
                          <div className="relative w-32 h-20 rounded-lg overflow-hidden" style={{ background: 'var(--color-background-secondary)' }}>
                            <img
                              src={video.videoThumbnail}
                              alt={video.videoTitle}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                              <Play className="w-6 h-6 text-white" />
                            </div>
                          </div>

                          {/* Video Details */}
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="font-medium">{video.videoTitle}</h4>
                              <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                background: video.verificationStatus === 'verified' ? 'var(--color-success)' : 
                                           video.verificationStatus === 'pending' ? 'var(--color-warning)' : 'var(--color-error)', 
                                color: 'var(--color-white)' 
                              }}>
                                {video.verificationStatus}
                              </span>
                              {video.isPrimary && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-primary)', 
                                  color: 'var(--color-white)' 
                                }}>Primary</span>
                              )}
                              {video.isAlternative && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-accent)', 
                                  color: 'var(--color-white)' 
                                }}>Alternative</span>
                              )}
                            </div>

                            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                              <div className="flex items-center space-x-1">
                                {getSourceIcon(video.videoSource)}
                                <span className="capitalize">{video.videoSource}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{formatDuration(video.videoDuration || 0)}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Eye className="w-4 h-4" />
                                <span>{video.viewCount}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <TrendingUp className="w-4 h-4" />
                                <span>{video.shareCount}</span>
                              </div>
                            </div>

                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Channel:</strong> {video.videoChannel}
                            </div>

                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Added:</strong> {formatDate(video.addedAt)} by {video.addedBy}
                            </div>

                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Last Verified:</strong> {formatDate(video.lastVerified)}
                            </div>

                            {/* Content Flags */}
                            <div className="flex items-center space-x-2 mb-2">
                              {video.contentFlags.hasAttribution && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-success)', 
                                  color: 'var(--color-white)' 
                                }}>Attribution</span>
                              )}
                              {video.contentFlags.hasSafetyDisclaimer && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-success)', 
                                  color: 'var(--color-white)' 
                                }}>Safety</span>
                              )}
                              {video.contentFlags.isEducational && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-success)', 
                                  color: 'var(--color-white)' 
                                }}>Educational</span>
                              )}
                              {video.contentFlags.needsReview && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-warning)', 
                                  color: 'var(--color-white)' 
                                }}>Needs Review</span>
                              )}
                            </div>

                            {/* Legal Status */}
                            <div className="flex items-center space-x-2 mb-2">
                              {video.legalStatus.copyrightCompliant ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-success)', 
                                  color: 'var(--color-white)' 
                                }}>Copyright OK</span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-error)', 
                                  color: 'var(--color-white)' 
                                }}>Copyright Issue</span>
                              )}
                              {video.legalStatus.attributionComplete ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-success)', 
                                  color: 'var(--color-white)' 
                                }}>Attribution OK</span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                                  background: 'var(--color-error)', 
                                  color: 'var(--color-white)' 
                                }}>Attribution Missing</span>
                              )}
                            </div>

                            {/* Social Media Links */}
                            {Object.keys(video.socialMediaLinks).length > 0 && (
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium">Social Links:</span>
                                {video.socialMediaLinks.youtube && (
                                  <a
                                    href={video.socialMediaLinks.youtube}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Youtube className="w-4 h-4" />
                                  </a>
                                )}
                                {video.socialMediaLinks.instagram && (
                                  <a
                                    href={video.socialMediaLinks.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-pink-500 hover:text-pink-700"
                                  >
                                    <Instagram className="w-4 h-4" />
                                  </a>
                                )}
                                {video.socialMediaLinks.facebook && (
                                  <a
                                    href={video.socialMediaLinks.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    <Facebook className="w-4 h-4" />
                                  </a>
                                )}
                                {video.socialMediaLinks.twitter && (
                                  <a
                                    href={video.socialMediaLinks.twitter}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-600"
                                  >
                                    <Twitter className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedVideo(video);
                              setShowVideoModal(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Edit video
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Delete video
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredMatrix.length === 0 && (
        <div className="sculptd-card-elevated sculptd-sheen" style={{
          boxShadow: 'var(--shadow-card-separation)'
        }}>
          <div className="sculptd-container-padding text-center">
            <div style={{ color: 'var(--color-text-muted)' }}>
              <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--color-text)' }}>No exercises found</h3>
              <p>Try adjusting your search criteria or filters</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPanel;
